import React, { useEffect, Suspense, lazy } from "react";
import { BrowserRouter as Router, Routes, Route } from "react-router-dom";
import {
  Alert,
  Box,
  GLOBAL_STYLES,
  LOCAL_STORAGE_KEYS,
  ScrollToTop as Navigations,
  Colors,
  PageTransition as PageTransitionWrapper,
  AppRoutes,
  AuthRoute,
  Loader,
} from "reusable";
import "./App.css";
import { Footer, Header } from "components";
import AnalyticsTracker from "./components/analytics/AnalyticsTracker";
// import ManagePayments from "./screens/manage-payments/ManagePayments";
import PostBlogPage from "./screens/admin/PostBlogPage";
import {
  AboutUs,
  CreateProfile,
  ExploreDesigners,
  Feedback,
  GetRating,
  Landing,
  Pricing,
  PrivacyPolicy,
  Profile,
  SignUp,
  TermsOfUse,
  JobBoardScreen,
  // ManageAccount,
  // PlansBilling,
  FAQ,
  BlogPage,
  BlogDetailsPage,
  NotFound,
} from "screens";
import FeedScreen from "./screens/feed/FeedScreen";
import { useUser, useLocalStorage, useInternetConnection, useHelmet } from "./backend";
// import { useDataMigration } from "./backend/hooks/data-migration/use-data-migration"; // Commented out as unused
import { Appsettings } from "config";
import JobDetailMobile from "./screens/job-board/JobDetailMobile";
// Lazy load account management components for better performance
const AccountSettingsLayout = lazy(() => import("./screens/manage-account/AccountSettingsLayout"));
// const ManageAccountGuard = lazy(() => import("./screens/manage-account/ManageAccountGuard").then(module => ({ default: module.ManageAccountGuard })));
// const PlansBillingGuard = lazy(() => import("./screens/manage-account/PlansBillingGuard").then(module => ({ default: module.PlansBillingGuard })));
// const ManagePayments = lazy(() => import("./screens/manage-payments/ManagePayments"));
// import ManagePayments from "./screens/manage-payments/ManagePayments";

const App = () => {
  // const { moveFieldValue, updateData } = useDataMigration(); // Commented out as unused
  const { subscribeUser } = useUser();
  const { isMobileScreen, isFreshSession } = useLocalStorage();
  const { HelmetComponent } = useHelmet({
    title: "Kydoscope",
    description:
      "Home for interior designers and architects. Find nearby designers and architects.",
    themeColor: Colors.ORANGE,
  });

  useInternetConnection();

  useEffect(() => {
    subscribeUser();
    function setMobileFlag() {
      const screenWidth = window.innerWidth;
      if (screenWidth < GLOBAL_STYLES.MOBILE_SCREEN_WIDTH) {
        localStorage.setItem(LOCAL_STORAGE_KEYS.IS_MOBILE_SCREEN, "true");
      } else {
        localStorage.setItem(LOCAL_STORAGE_KEYS.IS_MOBILE_SCREEN, "false");
      }
    }
    setMobileFlag();
    window.addEventListener("resize", setMobileFlag);
    setTimeout(() => {
      if (isFreshSession) {
        localStorage.setItem(LOCAL_STORAGE_KEYS.IS_FRESH_SESSION, "false");
      }
    }, 3000);
    return () => {
      window.removeEventListener("resize", setMobileFlag);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // const updateSearchTerms = (doc: unknown) => {
    //   if (doc) {
    //     return {
    //       lat: 18.5204303,
    //       lon: 73.8567437,
    //       place: "Pune, Maharashtra, India",
    //     };
    //   }
    // };
    // updateData(
    //   "creator-profiles",
    //   "name",
    //   undefined,
    //   undefined,
    //   undefined,
    //   "creator-profiles-summary"
    // );
  }, []);

  return (
    <Router>
      <AnalyticsTracker />
      <PageTransitionWrapper>
        <HelmetComponent />
        <Box className="kydoscope">
          <Pricing />
          <Header />
          <Navigations />
          {!Appsettings.isLive && (
            <Box className="maintenance-banner">
              <Alert
                type="info"
                message="Site is under Maintenance, please visit later."
                autoHide={false}
                displayCross={false}
              />
            </Box>
          )}
          <Box minHeight="calc(100vh - 240px)">
            <Routes>
              <Route path={AppRoutes.LANDING} element={<Landing />} />
              <Route path={AppRoutes.LOGIN} element={<SignUp isLoginMode />} />
              <Route path={AppRoutes.SIGN_UP} element={<SignUp />} />
              <Route path={AppRoutes.PROFILE} element={<Profile />} />
              <Route
                path={AppRoutes.PROFILE_PREVIEW}
                element={<Profile isPreview />}
              />
              <Route
                path={AppRoutes.FILL_PROFILE_DETAILS}
                element={<AuthRoute component={CreateProfile} />}
              />
              <Route
                path={AppRoutes.EXPLORE_DESIGNERS}
                element={<ExploreDesigners />}
              />
              <Route
                path={AppRoutes.FEEDBACK}
                element={<AuthRoute component={Feedback} />}
              />
              <Route
                path={AppRoutes.TERMS_AND_CONDITIONS}
                element={<TermsOfUse />}
              />
              <Route
                path={AppRoutes.PRIVACY_POLICY}
                element={<PrivacyPolicy />}
              />
              <Route
                path={AppRoutes.GET_RATING}
                element={<AuthRoute component={GetRating} />}
              />
              <Route path={AppRoutes.ABOUT_US} element={<AboutUs />} />
              <Route path={AppRoutes.JOBS} element={<JobBoardScreen />} />
              <Route path="/jobs/:jobId" element={isMobileScreen ? <JobDetailMobile /> : <JobBoardScreen />} />
              {/* Admin routes */}
              <Route path="/admin/post-blog" element={<PostBlogPage />} />
              <Route path="/blogs/:blogId" element={<BlogDetailsPage />} />
              <Route path="/profile/:userId" element={
                <Suspense fallback={
                  <div className="manage-payments-loader-container">
                    <div className="manage-payments-loader-text">Loading account settings...</div>
                    <Loader height="60px" width="60px" />
                  </div>
                }>
                  <AccountSettingsLayout />
                </Suspense>
              }>
                {/* <Route
                  path="manage-account"
                  element={
                    <Suspense fallback={
                      <div className="manage-payments-loader-container">
                        <div className="manage-payments-loader-text">Loading account management...</div>
                        <Loader height="60px" width="60px" />
                      </div>
                    }>
                      <ManageAccountGuard>
                        <ManageAccount />
                      </ManageAccountGuard>
                    </Suspense>
                  }
                />
                <Route
                  path="plans-billing"
                  element={
                    <Suspense fallback={
                      <div className="manage-payments-loader-container">
                        <div className="manage-payments-loader-text">Loading plans & billing...</div>
                        <Loader height="60px" width="60px" />
                      </div>
                    }>
                      <PlansBillingGuard>
                        <PlansBilling />
                      </PlansBillingGuard>
                    </Suspense>
                  }
                /> */}
              </Route>
              {/* <Route path={AppRoutes.MANAGE_PAYMENTS} element={<ManagePayments />} /> */}
              <Route path={AppRoutes.FAQ} element={<FAQ />} />
              <Route path={AppRoutes.BLOGS} element={<BlogPage />} />
              <Route path={AppRoutes.FEED} element={<FeedScreen />} />
              <Route path="*" element={<NotFound />} />
            </Routes>
          </Box>
          <Footer />
        </Box>
        <Box className="no-support">
          {isMobileScreen
            ? "Please rotate your phone"
            : "Currently we are only supporting Phone and Laptop size for Kydoscope !"}
        </Box>
      </PageTransitionWrapper>
    </Router>
  );
};

export default App;
